import {Alert, Box, CircularProgress, Divider, FormControl, MenuItem, Select, Stack, Typography} from "@mui/material";
import Property from "crqlar-react-common/dist/components/Property";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {BookingEngine, BookingEngineUIConfig} from "crqlar-react-common/dist/models/bookingEngine/bookingEngine.model";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import BorderRadiusField from "../styling/BorderRadiusField";
import CMemoryColorPicker from "crqlar-react-common/dist/components/CMemoryColorPicker";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ButtonImagePicker from "crqlar-react-common/dist/components/ButtonImagePicker";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import {getDefaultWidgetTitle} from "crqlar-react-common/dist/utils/bookingEngine.util";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {DEFAULT_COLOR_THEME} from "crqlar-react-common/dist/components/theme";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Theme {
    id: string;
    name: string;
    backgroundColor: string;
    textColor?: string;
    fieldBorderColor?: string;
    actionButtonBackgroundColor?: string;
    actionButtonTextColor?: string;
    actionButtonBackgroundDisabled?: string;
    borderRadius?: number;
    fieldBorderRadius?: number;
    buttonBorderRadius?: number;
    fontUrl?: string;
    fontFamily?: string;
}

const THEMES: Theme[] = [
    {
        id: 'standard',
        name: 'Standard',
        backgroundColor: '#ffffff',
        textColor: '#000000',
        actionButtonBackgroundColor: DEFAULT_COLOR_THEME.secondary.main,
        actionButtonTextColor: '#ffffff',
        actionButtonBackgroundDisabled: '#c8c8c8',
        borderRadius: 12,
        fieldBorderRadius: 6,
        buttonBorderRadius: 12,
        fieldBorderColor: '#00000089',
        fontUrl: 'https://fonts.googleapis.com/css2?family=League+Spartan:wght@100..900&display=swap',
        fontFamily: '"League Spartan", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
    },
    {
        id: 'nebula',
        name: 'Nebula',
        backgroundColor: '#12071e',
        textColor: '#d4b8f0',
        actionButtonBackgroundColor: '#7c3aed',
        actionButtonTextColor: '#ffffff',
        actionButtonBackgroundDisabled: '#2d1a42',
        fieldBorderColor: '#d4b8f0',
        borderRadius: 20,
        fieldBorderRadius: 8,
        buttonBorderRadius: 24,
        fontUrl: 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600&display=swap',
        fontFamily: '"Space Grotesk", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
    },
    {
        id: 'ivory',
        name: 'Ivory',
        backgroundColor: '#faf8f3',
        textColor: '#1c1a17',
        actionButtonBackgroundColor: '#1c1a17',
        actionButtonTextColor: '#faf8f3',
        actionButtonBackgroundDisabled: '#cec8b8',
        fieldBorderColor: '#00000089',
        borderRadius: 0,
        fieldBorderRadius: 0,
        buttonBorderRadius: 2,
        fontUrl: 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600&display=swap',
        fontFamily: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
    },
    {
        id: 'ocean',
        name: 'Ocean',
        backgroundColor: '#071e3d',
        textColor: '#a8d8f0',
        actionButtonBackgroundColor: '#1a6fa8',
        actionButtonTextColor: '#ffffff',
        actionButtonBackgroundDisabled: '#1a3352',
        fieldBorderColor: '#a8d8f0',
        borderRadius: 12,
        fieldBorderRadius: 6,
        buttonBorderRadius: 8,
        fontUrl: 'https://fonts.googleapis.com/css2?family=Raleway:wght@400;600&display=swap',
        fontFamily: '"Raleway", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
    },
    {
        id: 'blossom',
        name: 'Blossom',
        backgroundColor: '#fef0f5',
        textColor: '#5c1a35',
        actionButtonBackgroundColor: '#5c1a35',
        actionButtonTextColor: '#fef0f5',
        actionButtonBackgroundDisabled: '#dfa8be',
        fieldBorderColor: '#5c1a35',
        borderRadius: 24,
        fieldBorderRadius: 12,
        buttonBorderRadius: 50,
        fontUrl: 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;600&display=swap',
        fontFamily: '"Nunito", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
    },
]

interface Props {
    bookingEngine: BookingEngine;
    setBookingEngine: (bookingEngine: BookingEngine) => void;
    bookingEngineData: UseEntityData<BookingEngine>
}

interface ColorPickerRowProps {
    color?: string;
    onChange: (color: string) => void;
    label: string;
    disabled?: boolean;
}

const ColorPickerRow = ({color, onChange, label, disabled}: ColorPickerRowProps) => (
    <Stack direction="row" spacing={1} alignItems="center">
        <CMemoryColorPicker color={color as string} onChange={onChange} disabled={disabled}/>
        <Typography variant="subtitle2" gutterBottom>{label}</Typography>
    </Stack>
)

const isValidUrl = (v: string) => /^https?:\/\//i.test(v);
const isGoogleFontsUrl = (v: string) => /fonts\.googleapis\.com/i.test(v);

const extractGoogleFamily = (u: string) => {
    try {
        const url = new URL(u);
        const famParams = url.searchParams.getAll('family');
        if (!famParams.length) return undefined;
        const first = famParams[0].split(':')[0].replace(/\+/g, ' ').trim();
        if (!first) return undefined;
        return `"${first}", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
    } catch {
        return undefined;
    }
};

const getContrastColor = (hex: string): string => {
    const h = hex.replace('#', '')
    if (h.length < 6) return '#000000'
    const r = parseInt(h.slice(0, 2), 16)
    const g = parseInt(h.slice(2, 4), 16)
    const b = parseInt(h.slice(4, 6), 16)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.5 ? '#000000' : '#ffffff'
}

const BookingEngineStylingTab = ({
                                     bookingEngine,
                                     setBookingEngine,
                                     bookingEngineData,
                                 }: Props) => {

    const {t} = useLocalize()

    // Read-only users may browse styling settings but must not edit them. Property-wrapped
    // fields are locked by Property itself; here we additionally guard the controls outside
    // Property (theme tiles, color pickers) and the language dropdown (which `inert` does not
    // reliably block). The Logo picker already folds readOnly via ButtonImagePicker.
    const readOnly = useReadOnly()

    const sector = useSector(bookingEngine.sectorId)
    const borderRadius = bookingEngine.uiConfig?.borderRadius ?? 12
    const fieldBorderRadius = bookingEngine.uiConfig?.fieldBorderRadius ?? 6
    const buttonBorderRadius = bookingEngine.uiConfig?.buttonBorderRadius ?? fieldBorderRadius

    const [fontUrl, setFontUrlState] = useState(bookingEngine.uiConfig?.fontUrl ?? '')
    const [fontFamily, setFontFamilyState] = useState(bookingEngine.uiConfig?.fontFamily ?? '')
    const [loadingName, setLoadingName] = useState(false)
    const [editName, setEditName] = useState(true)
    const skipExtractRef = useRef(false)

    // Auto-extract family name from Google Fonts URL
    useEffect(() => {
        if (skipExtractRef.current) {
            skipExtractRef.current = false
            return
        }
        const valid = isValidUrl(fontUrl);
        const isG = isGoogleFontsUrl(fontUrl);
        if (!valid || !isG) {
            setEditName(true);
            return;
        }
        setEditName(false);
        setLoadingName(true);
        const h = window.setTimeout(() => {
            const parsed = extractGoogleFamily(fontUrl);
            if (parsed) setFontFamilyState(parsed);
            setLoadingName(false);
        }, 200);
        return () => window.clearTimeout(h);
    }, [fontUrl]);

    // Inject font stylesheet into document head whenever fontUrl changes
    useEffect(() => {
        if (!fontUrl) return;
        const styleId = 'be-font-style';
        const el = (document.getElementById(styleId) as HTMLStyleElement) || (() => {
            const s = document.createElement('style');
            s.id = styleId;
            document.head.appendChild(s);
            return s;
        })();
        el.innerHTML = `@import url('${fontUrl}');`;
    }, [fontUrl]);

    // Write fontUrl/fontFamily back to bookingEngine
    useEffect(() => {
        if (
            fontUrl === (bookingEngine.uiConfig?.fontUrl ?? '') &&
            fontFamily === (bookingEngine.uiConfig?.fontFamily ?? '')
        ) return;
        setBookingEngine({
            ...bookingEngine,
            uiConfig: {...bookingEngine.uiConfig, fontUrl, fontFamily}
        });
    }, [fontUrl, fontFamily]);


    const updateUiConfig = (updates: Partial<BookingEngineUIConfig>) =>
        setBookingEngine({...bookingEngine, uiConfig: {...bookingEngine.uiConfig, ...updates}})

    const updateFormConfig = (updates: Partial<NonNullable<BookingEngine['formConfig']>>) =>
        setBookingEngine({...bookingEngine, formConfig: {...bookingEngine.formConfig, ...updates}})

    const applyTheme = (theme: Theme) => {
        if (theme.fontUrl !== undefined) {
            skipExtractRef.current = true
            setFontUrlState(theme.fontUrl)
            setEditName(!isGoogleFontsUrl(theme.fontUrl))
        }
        if (theme.fontFamily !== undefined) {
            setFontFamilyState(theme.fontFamily)
        }
        setBookingEngine({
            ...bookingEngine,
            uiConfig: {
                ...bookingEngine.uiConfig,
                backgroundColor: theme.backgroundColor,
                textColor: theme.textColor,
                actionButtonBackgroundColor: theme.actionButtonBackgroundColor,
                actionButtonTextColor: theme.actionButtonTextColor,
                actionButtonBackgroundDisabled: theme.actionButtonBackgroundDisabled,
                fieldBorderColor: theme.fieldBorderColor,
                borderRadius: theme.borderRadius,
                fieldBorderRadius: theme.fieldBorderRadius,
                buttonBorderRadius: theme.buttonBorderRadius,
                ...(theme.fontUrl !== undefined && {fontUrl: theme.fontUrl}),
                ...(theme.fontFamily !== undefined && {fontFamily: theme.fontFamily}),
            }
        })
    }

    const activeThemeId = THEMES.find(th =>
        th.backgroundColor === bookingEngine.uiConfig?.backgroundColor
    )?.id

    const uiConfigSection = (
        <>
            <Typography variant={'h6'} textAlign={'start'}>
                {t('Interface configuration')}
            </Typography>

            <Property title={t('Default language')}>
                <FormControl fullWidth>
                    <Select
                        value={bookingEngine.uiConfig?.defaultLanguageCode ?? ''}
                        onChange={(e) => updateUiConfig({defaultLanguageCode: e.target.value})}
                        size={'small'}
                        displayEmpty
                        disabled={readOnly}
                    >
                        <MenuItem value="">{t('Auto-detect')}</MenuItem>
                        {['en', 'de', 'it'].map((languageCode) => (
                            <MenuItem key={languageCode} value={languageCode}>
                                {getNameForLanguage(languageCode)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Property>

            <Divider/>

            <Property title={t('Default country code prefix')}>
                <TextField
                    id={'name'}
                    size={'small'}
                    value={bookingEngine.uiConfig?.defaultCountryCodePrefix}
                    onChange={(e) => updateUiConfig({defaultCountryCodePrefix: e.target.value})}
                    fullWidth
                    required
                    disabled={readOnly}
                />
            </Property>

            <Divider/>

            <Property title={t('Widget title')}>
                <LocalizedTextField
                    id={'widget-title-localized'}
                    size={'small'}
                    value={bookingEngine.formConfig?.widgetTitle || getDefaultWidgetTitle(sector?.sectorType)}
                    onChange={(e) => updateFormConfig({widgetTitle: e})}
                    fullWidth
                    required
                    disabled={readOnly}
                />
            </Property>
        </>
    )

    return (
        <Stack
            gap={3}
        >
            {/* Predefined themes */}
            <Stack gap={1.5}>
                <Typography variant={'subtitle1'}>{t('Themes')}</Typography>
                <Stack direction={'row'} gap={1.5}>
                    {THEMES.map(theme => (
                        <Box
                            key={theme.id}
                            onClick={readOnly ? undefined : () => applyTheme(theme)}
                            sx={{
                                flex: 1,
                                bgcolor: theme.backgroundColor,
                                borderRadius: `${theme.borderRadius}px`,
                                p: 1.5,
                                cursor: readOnly ? 'default' : 'pointer',
                                border: '2px solid',
                                borderColor: activeThemeId === theme.id ? 'primary.main' : 'grey.300',
                                transition: 'border-color 0.15s',
                                ...(readOnly ? {} : {'&:hover': {borderColor: 'primary.main'}}),
                            }}
                        >
                            <Typography
                                variant={'body2'}
                                fontWeight={600}
                                sx={{color: theme.textColor, mb: 1}}
                            >
                                {theme.name}
                            </Typography>
                            <Box sx={{
                                bgcolor: theme.textColor,
                                color: theme.backgroundColor,
                                borderRadius: `${theme.buttonBorderRadius}px`,
                                px: 1,
                                py: 0.25,
                                fontSize: 10,
                                textAlign: 'center'
                            }}>
                                {t('Continue')}
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </Stack>

            <Divider/>

            <Property title={t('Logo')}>
                <ButtonImagePicker
                    id={'image-picker'}
                    image={bookingEngine.uiConfig?.logo}
                    setImage={(image) => updateUiConfig({logo: image})}
                    width={150}
                    height={150}
                    objectFit={'contain'}
                    onAfterUpload={async (media) => {
                        await bookingEngineData.saveThenSet({
                            ...bookingEngineData.item,
                            uiConfig: {...bookingEngineData.item.uiConfig, logo: media}
                        })
                    }}
                    onAfterDelete={async () => {
                        await bookingEngineData.saveThenSet({
                            ...bookingEngineData.item,
                            uiConfig: {...bookingEngineData.item.uiConfig, logo: undefined}
                        })
                    }}
                />
            </Property>

            <Divider/>

            <Stack direction={'row'} alignItems={'flex-start'} gap={0} justifyContent={'space-between'}>
                <Stack flex={1}>
                    <Property title={t('Widget border radius (px)')} orientation={'vertical'}>
                        <BorderRadiusField value={borderRadius} onChange={v => updateUiConfig({borderRadius: v})}/>
                    </Property>
                </Stack>
                <Stack flex={1}>
                    <Property title={t('Text fields border radius (px)')} orientation={'vertical'}>
                        <BorderRadiusField value={fieldBorderRadius} onChange={v => updateUiConfig({fieldBorderRadius: v})}/>
                    </Property>
                </Stack>
                <Stack flex={1}>
                    <Property title={t('Buttons border radius (px)')} orientation={'vertical'}>
                        <BorderRadiusField value={buttonBorderRadius} onChange={v => updateUiConfig({buttonBorderRadius: v})}/>
                    </Property>
                </Stack>
            </Stack>

            <Divider/>

            <Stack direction="row" spacing={5} alignItems="center" flexWrap="wrap">
                <ColorPickerRow
                    color={bookingEngine.uiConfig?.backgroundColor}
                    onChange={color => updateUiConfig({
                        backgroundColor: color,
                        textColor: color ? getContrastColor(color) : bookingEngine.uiConfig?.textColor,
                        fieldBorderColor: color ? getContrastColor(color) : bookingEngine.uiConfig?.fieldBorderColor,
                    })}
                    label={t('Background')}
                    disabled={readOnly}
                />
                <ColorPickerRow
                    color={bookingEngine.uiConfig?.textColor}
                    onChange={color => updateUiConfig({textColor: color, fieldBorderColor: color})}
                    label={t('Text')}
                    disabled={readOnly}
                />
                <ColorPickerRow
                    color={bookingEngine.uiConfig?.actionButtonBackgroundColor}
                    onChange={color => updateUiConfig({
                        actionButtonBackgroundColor: color,
                        actionButtonTextColor: color ? getContrastColor(color) : bookingEngine.uiConfig?.textColor,
                    })}
                    label={t('Button background')}
                    disabled={readOnly}
                />
                <ColorPickerRow
                    color={bookingEngine.uiConfig?.actionButtonTextColor}
                    onChange={color => updateUiConfig({actionButtonTextColor: color})}
                    label={t('Button text')}
                    disabled={readOnly}
                />
                <ColorPickerRow
                    color={bookingEngine.uiConfig?.actionButtonBackgroundDisabled}
                    onChange={color => updateUiConfig({actionButtonBackgroundDisabled: color})}
                    label={t('Button disabled background')}
                    disabled={readOnly}
                />
            </Stack>

            <Divider/>

            {/* Font section */}
            <Stack gap={1.5}>
                <Typography variant={'subtitle1'}>{t('Fonts')}</Typography>
                <Alert severity={'info'}>
                    <Typography variant={'body2'}>
                        {t('The URL must be publicly available (recommended: Google Fonts): ')}
                        <a href="https://fonts.google.com" target="_blank" rel="noopener noreferrer">
                            https://fonts.google.com
                        </a>
                        {t('. The font family name must start with the selected font in "<font-name>" followed by fallback names.')}
                    </Typography>
                </Alert>

                <Property title={t('Font URL')} orientation={'vertical'}>
                    <TextField
                        size="small"
                        value={fontUrl}
                        onChange={(e) => setFontUrlState(e.target.value)}
                        fullWidth
                        disabled={readOnly}
                        InputProps={{
                            endAdornment: loadingName ? <CircularProgress size={18}/> : null,
                        }}
                    />
                </Property>

                <Property title={t('Font family')} orientation={'vertical'}>
                    <Stack direction="row" spacing={1} sx={{width: '100%'}}>
                        <TextField
                            size="small"
                            value={fontFamily}
                            onChange={(e) => setFontFamilyState(e.target.value)}
                            fullWidth
                            disabled={readOnly || (!editName && isValidUrl(fontUrl) && isGoogleFontsUrl(fontUrl))}
                        />
                        <Button variant="outlined" disabled={readOnly} onClick={() => setEditName(v => !v)}>
                            {editName ? t('Confirm') : t('Edit')}
                        </Button>
                    </Stack>
                </Property>

            </Stack>

            {uiConfigSection}

        </Stack>
    )
}

export default BookingEngineStylingTab;
